import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENV } from '../../core-ng/injection-token';
import { Observable } from 'rxjs';
import { ConditionSetElementType } from '@hyper-inferno/survey-elements-core';

interface CreateConditionPayload {
  skip_logic_id?: string;
  condition_element_id: number;
  operator: string;
  option_id?: string;
  row_key?: number;
  value_manual?: any;
  option_targets?: string[];
  logical_operator?: 'AND' | 'OR';
  description?: string;
  condition_set_element_id?: number;
  condition_set_element_type: ConditionSetElementType;
}
@Injectable({
  providedIn: 'root',
})
export class ConditionRulesManagerService {
  constructor() {}
  private http = inject(HttpClient);
  environment = inject(ENV);

  // Get skip logic settings (id, page_id, element_id)
  getSkipLogicSettings(id: string): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/get-skip-logic-settings/${id}`,
    );
  }

  // Get condition data (condition set + conditions)
  getConditions(skipLogicId: string): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/${skipLogicId}/get-conditions`,
    );
  }

  // Create a new condition
  createCondition(payload: CreateConditionPayload): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/skip-logic-settings-api/create-condition`,
      payload,
    );
  }

  // Get element settings (to find list_id)
  getElementSettings(elementId: number, elementType: string): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/get-element-settings/${elementId}?type=${elementType}`,
    );
  }

  // Get list options
  getListOptions(list_id: string): Observable<any[]> {
    return this.http.post<any[]>(
      `${this.environment.consoleApi}/skip-logic-settings-api/get-list-options`,
      { list_id },
    );
  }

  // Update jump-to page
  updateJumpToPage(skipLogicId: string, pageId: number): Observable<any> {
    return this.http.patch(
      `${this.environment.consoleApi}/skip-logic-settings-api/${skipLogicId}/jump-to-page`,
      { page_id: pageId },
    );
  }

  // Update condition set (description and logical operator)
  updateConditionSet(
    conditionSetId: string,
    payload: { description?: string; logical_operator?: 'AND' | 'OR' },
  ): Observable<any> {
    return this.http.patch(
      `${this.environment.consoleApi}/skip-logic-settings-api/update-condition-set/${conditionSetId}`,
      payload,
    );
  }

  updateCondition(
    conditionId: string,
    payload: {
      condition_element_id?: number;
      operator?: string;
      option_id?: string;
      row_key?: number;
      value_manual?: any;
      option_targets?: string[];
    },
  ): Observable<any> {
    return this.http.patch(
      `${this.environment.consoleApi}/skip-logic-settings-api/update-condition/${conditionId}`,
      payload,
    );
  }

  // Delete a condition
  deleteCondition(conditionId: string): Observable<any> {
    return this.http.delete(
      `${this.environment.consoleApi}/skip-logic-settings-api/delete-condition/${conditionId}`,
    );
  }

  reorderConditions(
    payload: {
      id: string;
      rank: number;
      condition_set_id: string;
    }[],
  ): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/skip-logic-settings-api/reorder-conditions`,
      payload,
    );
  }
  createConditionSet(payload: any): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/skip-logic-settings-api/create-condition-set`,
      payload,
    );
  }
  getConditionSet(id: string): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/condition-set/${id}`,
    );
  }
  updateJumpToElement(
    conditionSetId: string,
    elementId: number,
  ): Observable<any> {
    return this.http.patch(
      `${this.environment.consoleApi}/skip-logic-settings-api/condition-set/${conditionSetId}/jump-to-element`,
      { element_id: elementId },
    );
  }

  // Get condition set by element ID (for question elements)
  getConditionSetByElementId(elementId: number): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/condition-set/by-element/${elementId}`,
    );
  }

  // Get condition set by skip logic ID (for skip logic elements)
  getConditionSetBySkipLogicId(skipLogicId: string): Observable<any> {
    return this.http.get(
      `${this.environment.consoleApi}/skip-logic-settings-api/condition-set/by-skip-logic/${skipLogicId}`,
    );
  }
}
